import React, { useContext, useEffect, useMemo, useState } from "react";
import TaskListToolbar from "../../components/task/TaskListToolbar";
import TaskListView from "../../components/task/TaskListView";
import AppContext from "../../context/AppContext";
import translations from "../../components/common/Translations";
import { useLocation } from "react-router-dom";
import { fetchTasks } from "../../service/Task";
import Cookies from "js-cookie";

const TASK_MANAGE_SCREEN_STATE_KEY = "taskManageScreenState";
const TASK_MANAGE_COLUMN_PREFS_KEY = "taskManageColumnPrefs";
const DEFAULT_COLUMN_SELECTION_MODE = "custom";
const OPT_IN_COLUMN_KEYS = ["taskLocation"];

const normalizeColumnPrefsShape = (rawValue) => {
  if (Array.isArray(rawValue)) {
    return {
      visibleColumnKeys: rawValue,
      knownHeaderKeys: [],
      selectionMode: DEFAULT_COLUMN_SELECTION_MODE,
      updatedAt: 0,
    };
  }
  if (rawValue && typeof rawValue === "object") {
    const rawMode = String(rawValue.selectionMode || "").toLowerCase();
    return {
      visibleColumnKeys: Array.isArray(rawValue.visibleColumnKeys) ? rawValue.visibleColumnKeys : [],
      knownHeaderKeys: Array.isArray(rawValue.knownHeaderKeys) ? rawValue.knownHeaderKeys : [],
      selectionMode: rawMode === "all" ? "all" : DEFAULT_COLUMN_SELECTION_MODE,
      updatedAt: Number.isFinite(Number(rawValue.updatedAt)) ? Number(rawValue.updatedAt) : 0,
    };
  }
  return {
    visibleColumnKeys: [],
    knownHeaderKeys: [],
    selectionMode: DEFAULT_COLUMN_SELECTION_MODE,
    updatedAt: 0,
  };
};

const readTaskManageColumnPrefs = () => {
  try {
    const parseSafe = (raw) => {
      if (!raw) return normalizeColumnPrefsShape(null);
      try {
        return normalizeColumnPrefsShape(JSON.parse(raw));
      } catch {
        return normalizeColumnPrefsShape(null);
      }
    };

    const sessionPrefs = parseSafe(sessionStorage.getItem(TASK_MANAGE_COLUMN_PREFS_KEY));
    const localPrefs = parseSafe(localStorage.getItem(TASK_MANAGE_COLUMN_PREFS_KEY));
    const sessionUpdatedAt = Number(sessionPrefs.updatedAt || 0);
    const localUpdatedAt = Number(localPrefs.updatedAt || 0);
    if (sessionUpdatedAt !== localUpdatedAt) {
      return sessionUpdatedAt > localUpdatedAt ? sessionPrefs : localPrefs;
    }

    const hasSessionKnown = (sessionPrefs.knownHeaderKeys || []).length > 0;
    const hasLocalKnown = (localPrefs.knownHeaderKeys || []).length > 0;
    if (hasSessionKnown && hasLocalKnown) return sessionPrefs;
    if (hasSessionKnown) return sessionPrefs;
    if (hasLocalKnown) return localPrefs;

    const hasSessionVisible = (sessionPrefs.visibleColumnKeys || []).length > 0;
    const hasLocalVisible = (localPrefs.visibleColumnKeys || []).length > 0;
    if (hasSessionVisible) return sessionPrefs;
    if (hasLocalVisible) return localPrefs;

    return normalizeColumnPrefsShape(null);
  } catch (error) {
    console.error("Error restoring task-manage column preferences:", error);
    return normalizeColumnPrefsShape(null);
  }
};

const persistTaskManageColumnPrefs = ({
  visibleColumnKeys = [],
  knownHeaderKeys = [],
  selectionMode = DEFAULT_COLUMN_SELECTION_MODE,
} = {}) => {
  try {
    const normalizedMode = String(selectionMode || "").toLowerCase() === "all" ? "all" : DEFAULT_COLUMN_SELECTION_MODE;
    const payload = JSON.stringify({
      visibleColumnKeys: Array.isArray(visibleColumnKeys) ? visibleColumnKeys : [],
      knownHeaderKeys: Array.isArray(knownHeaderKeys) ? knownHeaderKeys : [],
      selectionMode: normalizedMode,
      updatedAt: Date.now(),
    });
    sessionStorage.setItem(TASK_MANAGE_COLUMN_PREFS_KEY, payload);
    localStorage.setItem(TASK_MANAGE_COLUMN_PREFS_KEY, payload);
  } catch (error) {
    console.error("Error saving task-manage column preferences:", error);
  }
};

const TaskManageMain = () => {
  const [openTab, setOpenTab] = useState(2);
  const [isChecked, setIsChecked] = useState(false);
  const [selectedtask, setselectedtask] = useState([]);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [workflowName, setWorkflowName] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [totalValue, setTotalValue] = useState(0);
  const [finishedStatus, setFinishedStatus] = useState("");
  const { theme, langMode, setPriority, setActive, setAreaCode, setSelectedUserId, setAccountNo, active, priority, selectedOrgId, workflowId, setTaskData, setTotalPageSize, totalPageSize } = useContext(AppContext);
  const [assigneesToUnassign, setAssigneesToUnassign] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [provideTasksWithMissingLatAndLong, setProvideTasksWithMissingLatAndLong] = useState(false);
  const [tableHeaders, setTableHeaders] = useState([]);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState([]);
  const [aiSourceFilter, setAiSourceFilter] = useState("");
  const [filterResetToken, setFilterResetToken] = useState(0);
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  const [showTableFilters, setShowTableFilters] = useState(true);
  const [savedVisibleColumnKeys, setSavedVisibleColumnKeys] = useState([]);
  const [columnSelectionMode, setColumnSelectionMode] = useState(DEFAULT_COLUMN_SELECTION_MODE);
  /* The headers that were on screen the last time this user applied a column
     selection. Persisted all along but never read back until now -- it is what
     tells a column the user deliberately unticked apart from one that has
     shipped since and was never on the list they chose from. */
  const [knownHeaderKeys, setKnownHeaderKeys] = useState([]);
  const [searchFields, setSearchFields] = useState([]);
  const [noColumnAccess, setNoColumnAccess] = useState(false);
  // The exact filter payload TaskListView last sent to POST list/search, so the
  // toolbar's Export button can queue a report for the same rows the grid is
  // showing. Held here because the two are siblings, not parent and child.
  const [exportRequest, setExportRequest] = useState(null);

  useEffect(() => {
    const prefs = readTaskManageColumnPrefs();
    setSavedVisibleColumnKeys(prefs.visibleColumnKeys || []);
    setVisibleColumnKeys(prefs.visibleColumnKeys || []);
    setColumnSelectionMode(prefs.selectionMode || DEFAULT_COLUMN_SELECTION_MODE);
    setKnownHeaderKeys(prefs.knownHeaderKeys || []);
  }, []);

  const effectiveVisibleColumnKeys = useMemo(() => {
    const headerKeys = (tableHeaders || []).map((header) => header.value);
    if (!headerKeys.length) return [];
    // Opt-in columns (e.g. Location) are only shown once the user ticks them in Manage Columns,
    // so they are left out of every "show whatever is new / show everything" default below.
    const autoHeaderKeys = headerKeys.filter((key) => !OPT_IN_COLUMN_KEYS.includes(key));

    const intentKeys = (visibleColumnKeys || []).length
      ? visibleColumnKeys
      : (savedVisibleColumnKeys || []);
    const orderedExisting = (intentKeys || []).filter((key) => headerKeys.includes(key));
    const missingFromIntent = autoHeaderKeys.filter((key) => !orderedExisting.includes(key));

    if (columnSelectionMode === "all") {
      return orderedExisting.length
        ? [...orderedExisting, ...missingFromIntent]
        : autoHeaderKeys;
    }

    /* A column that shipped after the user last applied a selection was never
       on the list they picked from, so its absence from that selection is not
       an opt-out -- which is what kept Cycle hidden behind a stale preference.
       Columns they actually unticked are recorded in knownHeaderKeys and stay
       hidden.

       Guarded on knownHeaderKeys being present: the legacy array-shaped
       preference carries none, and treating every column as new there would
       silently discard the user's entire selection. */
    const neverOfferedKeys = (knownHeaderKeys || []).length
      ? missingFromIntent.filter((key) => !knownHeaderKeys.includes(key))
      : [];

    if (!orderedExisting.length) return autoHeaderKeys;
    return neverOfferedKeys.length
      ? [...orderedExisting, ...neverOfferedKeys]
      : orderedExisting;
  }, [
    tableHeaders,
    columnSelectionMode,
    visibleColumnKeys,
    savedVisibleColumnKeys,
    knownHeaderKeys,
  ]);

  useEffect(() => {
    const headerKeys = (tableHeaders || []).map((header) => header.value);
    if (!headerKeys.length || !Array.isArray(visibleColumnKeys) || !visibleColumnKeys.length) return;
    const cleaned = visibleColumnKeys.filter((key) => headerKeys.includes(key));
    if (cleaned.length === visibleColumnKeys.length) return;

    /* This is the only place knownHeaderKeys is refreshed outside an explicit
       Apply, so pruning a stale key here would otherwise mark every
       never-offered column as seen-and-declined and hide it again. Fold them
       into the selection first, by the same rule effectiveVisibleColumnKeys
       uses, so surfacing them survives the refresh. */
    const neverOffered = (knownHeaderKeys || []).length
      ? headerKeys.filter(
          (key) => !OPT_IN_COLUMN_KEYS.includes(key) && !cleaned.includes(key) && !knownHeaderKeys.includes(key)
        )
      : [];
    const nextVisible = [...cleaned, ...neverOffered];

    const nextMode =
      nextVisible.length > 0 &&
      nextVisible.length === headerKeys.length &&
      headerKeys.every((key) => nextVisible.includes(key))
        ? "all"
        : DEFAULT_COLUMN_SELECTION_MODE;
    setVisibleColumnKeys(nextVisible);
    setSavedVisibleColumnKeys(nextVisible);
    setColumnSelectionMode(nextMode);
    setKnownHeaderKeys(headerKeys);
    persistTaskManageColumnPrefs({
      visibleColumnKeys: nextVisible,
      knownHeaderKeys: headerKeys,
      selectionMode: nextMode,
    });
  }, [tableHeaders, visibleColumnKeys, knownHeaderKeys]);

  const handleColumnSelectionApply = (nextVisibleKeys = []) => {
    const normalizedKeys = Array.isArray(nextVisibleKeys) ? nextVisibleKeys : [];
    const headerKeys = (tableHeaders || []).map((header) => header.value);
    const isAllSelected =
      headerKeys.length > 0 &&
      normalizedKeys.length === headerKeys.length &&
      headerKeys.every((key) => normalizedKeys.includes(key));
    const nextMode = isAllSelected ? "all" : DEFAULT_COLUMN_SELECTION_MODE;

    setVisibleColumnKeys(normalizedKeys);
    setSavedVisibleColumnKeys(normalizedKeys);
    setColumnSelectionMode(nextMode);
    /* Everything on screen has now been offered to the user, so a column left
       out of normalizedKeys is a real opt-out from here on -- without this the
       memo would keep re-surfacing a column they just unticked. */
    setKnownHeaderKeys(headerKeys);

    persistTaskManageColumnPrefs({
      visibleColumnKeys: normalizedKeys,
      knownHeaderKeys: headerKeys,
      selectionMode: nextMode,
    });
  };
  const handleFilterChange = (filterType, value) => {
    setFilters((prevFilters) => ({ ...prevFilters, [filterType]: value }));
    setCurrentPage(0);
  };
  const location = useLocation()

  useEffect(() => {
    if (!location.pathname?.startsWith("/task-manage")) return;

    try {
      const rawCookieFilters = Cookies.get("mapListFilters");
      const parsedCookieFilters = rawCookieFilters ? JSON.parse(rawCookieFilters) : {};

      const selectedPriority = parsedCookieFilters?.selectedPriority;
      if (selectedPriority) {
        setPriority(selectedPriority === "Priority" ? "Everything" : selectedPriority);
      }

      const selectedStatus = parsedCookieFilters?.selectedStatus;
      if (selectedStatus) {
        setActive(selectedStatus === "Status" ? "Everything" : selectedStatus);
      }

      setAreaCode(parsedCookieFilters?.areaInput || "");
      setSelectedUserId(parsedCookieFilters?.selectedUserId || "");

      const savedSearch = sessionStorage.getItem("taskSearchValue");
      setAccountNo(savedSearch || "");
    } catch (error) {
      console.error("Error hydrating task filters:", error);
    } finally {
      setFiltersHydrated(true);
    }
  }, [location.pathname, setPriority, setActive, setAreaCode, setSelectedUserId, setAccountNo]);
  useEffect(() => {
    const getFromDate = sessionStorage.getItem("from_Date");
    setFromDate(getFromDate || "");

    const getToDate = sessionStorage.getItem("to_Date");
    setToDate(getToDate || "");

    try {
      const rawState = sessionStorage.getItem(TASK_MANAGE_SCREEN_STATE_KEY);
      if (!rawState) return;
      const parsed = JSON.parse(rawState);
      if (typeof parsed?.provideTasksWithMissingLatAndLong === "boolean") {
        setProvideTasksWithMissingLatAndLong(parsed.provideTasksWithMissingLatAndLong);
      }
      if (typeof parsed?.aiSourceFilter === "string") {
        setAiSourceFilter(parsed.aiSourceFilter);
      }
    } catch (error) {
      console.error("Error restoring task-manage screen state:", error);
    }
  }, [])

  useEffect(() => {
    try {
      sessionStorage.setItem(
        TASK_MANAGE_SCREEN_STATE_KEY,
        JSON.stringify({
          provideTasksWithMissingLatAndLong,
          aiSourceFilter,
        })
      );
    } catch (error) {
      console.error("Error saving task-manage screen state:", error);
    }
  }, [provideTasksWithMissingLatAndLong, aiSourceFilter]);

  useEffect(() => {
    try {
      sessionStorage.setItem("from_Date", fromDate || "");
      sessionStorage.setItem("to_Date", toDate || "");
    } catch (error) {
      console.error("Error saving task date filters:", error);
    }
  }, [fromDate, toDate]);
  useEffect(() => {
    if (location.pathname === '/task-manage') {
      // setPriority('Priority')
      // setActive('Status')
    }
  }, [location.pathname])

  const handleFiltersReset = () => {
    // filterResetToken (consumed in TaskListView) only clears the fields it
    // owns locally (taskId, createdBy, source, priority, status, dynamic
    // column filters, assignee). accountNo/fromDate/toDate/aiSourceFilter/
    // provideTasksWithMissingLatAndLong live up here instead, so "Reset
    // Filters" was leaving them applied -- clear them explicitly too.
    setAccountNo("");
    setFromDate("");
    setToDate("");
    setAiSourceFilter("");
    setProvideTasksWithMissingLatAndLong(false);
    setFilterResetToken((prev) => prev + 1);
  };



  const handlePageSizeChange = (e) => {
    const size = parseInt(e.target.value, 10);
    setPageSize(size);
    setCurrentPage(1);
  };
  const fetchData = async (
    workflowId,
    currentPage,
    pageSize,
    priority,
    active,
    areaCode,
    accountNo,
    selectedUserId,
    selectedSubactivity,
    fromDate,
    toDate
  ) => {
    if (!workflowId) {
      setTaskData([]);
      setTotalValue(0);
      return;
    }
    try {

    } catch (error) {
      console.error("Error fetching tasks:", error);
      // setLoading(false);
    }
  };


  useEffect(() => {
    const mainElement = document.querySelector('main');
    const mediaQuery = window.matchMedia("(min-width: 640px)");

    const updateMainOverflow = () => {
      if (!mainElement) return;
      mainElement.classList.toggle('overflow-hidden', mediaQuery.matches);
    };

    updateMainOverflow();
    mediaQuery.addEventListener?.("change", updateMainOverflow);

    return () => {
      mediaQuery.removeEventListener?.("change", updateMainOverflow);
      mainElement?.classList.remove('overflow-hidden');
    };
  }, []);

  return (
    <div className="relative h-full min-w-0 w-full overflow-x-hidden overflow-y-auto bg-white sm:overflow-y-hidden">
      <div
        className={`box-border flex h-full min-h-0 w-full flex-col overflow-hidden bg-white ${theme === "dark"
          ? "dark-mode  "
          : theme === "high-contrast"
            ? "high-contrast  "
            : ""
          }`}  >
        <div className="flex h-full min-h-0 min-w-0 w-full flex-col overflow-x-hidden sm:overflow-y-hidden">
          <TaskListToolbar
            openTab={openTab}
            setOpenTab={setOpenTab}
            isChecked={isChecked}
            selectedtask={selectedtask}
            setIsChecked={setIsChecked}
            onFilterChange={handleFilterChange}
            setWorkflowName={setTaskData}
            setselectedtask={setselectedtask}
            fromDate={fromDate}
            setFromDate={setFromDate}
            toDate={toDate}
            setToDate={setToDate}
            finishedStatus={finishedStatus}
            assigneesToUnassign={assigneesToUnassign}
            setAssigneesToUnassign={setAssigneesToUnassign}
            selectedTasks={selectedTasks}
            setSelectedTasks={setSelectedTasks}
            totalPageSize={totalPageSize}
            provideTasksWithMissingLatAndLong={provideTasksWithMissingLatAndLong}
            setProvideTasksWithMissingLatAndLong={setProvideTasksWithMissingLatAndLong}
            tableHeaders={tableHeaders}
            visibleColumnKeys={effectiveVisibleColumnKeys}
            setVisibleColumnKeys={setVisibleColumnKeys}
            onColumnSelectionApply={handleColumnSelectionApply}
            setFinishedStatus={setFinishedStatus}
            aiSourceFilter={aiSourceFilter}
            setAiSourceFilter={setAiSourceFilter}
            onFiltersReset={handleFiltersReset}
            showTableFilters={showTableFilters}
            onToggleTableFilters={() => setShowTableFilters((prev) => !prev)}
            searchFields={searchFields}
            noColumnAccess={noColumnAccess}
            exportRequest={exportRequest}
          />

          <TaskListView
            isChecked={isChecked}
            setIsChecked={setIsChecked}
            selectedtask={selectedtask}
            setselectedtask={setselectedtask}
            fromDate={fromDate}
            setFromDate={setFromDate}
            toDate={toDate}
            setToDate={setToDate}
            setTotalPageSize={setTotalPageSize}
            totalPageSize={totalPageSize}
            setFinishedStatus={setFinishedStatus}
            assigneesToUnassign={assigneesToUnassign}
            setAssigneesToUnassign={setAssigneesToUnassign}
            selectedTasks={selectedTasks}
            setSelectedTasks={setSelectedTasks}
            provideTasksWithMissingLatAndLong={provideTasksWithMissingLatAndLong}
            setProvideTasksWithMissingLatAndLong={setProvideTasksWithMissingLatAndLong}
            onTableHeadersChange={setTableHeaders}
            onSearchFieldsReady={setSearchFields}
            visibleColumnKeys={effectiveVisibleColumnKeys}
            aiSourceFilter={aiSourceFilter}
            filterResetToken={filterResetToken}
            filtersHydrated={filtersHydrated}
            showTableFilters={showTableFilters}
            onColumnAccessStateChange={setNoColumnAccess}
            onExportRequestChange={setExportRequest}
          />
        </div>
      </div>
    </div>
  );
};

export default TaskManageMain;
